// Notification store. Entries are pushed via the `add` action; the navbar
// bell consumes `getItems` and `unreadCount`. The store also:
//   • persists a history/log of notifications to localStorage (per user)
//     so the record survives reloads,
//   • honours per-user notification preferences (which categories a
//     participant wants to receive) and drops anything they've opted out
//     of before it's ever shown. Preferences are also persisted server-side
//     (per event + participant) so the choice follows the user across
//     devices/logins; localStorage is a fast, offline cache.

import ApiClient from '../../client';
import i18n from '@/i18n';

const MAX_ITEMS = 100;

// ── Notification categories ─────────────────────────────────────────
// Every notification `type` maps to a user-facing category. The settings
// UI toggles categories; `add` checks the category before surfacing.
// `label` / `description` are getters so they resolve in the event language
// whenever the settings UI reads them, not once at import time.
const category = (key) => ({
  key,
  get label() {
    return i18n.t(`Store.Notifications.categories.${key}.label`);
  },
  get description() {
    return i18n.t(`Store.Notifications.categories.${key}.description`);
  }
});

const CATEGORIES = [
  category('messages'),
  category('sessions'),
  category('announcements')
  // {
  //   key: 'polls',
  //   label: 'Polls & questions',
  //   description: 'New live polls to answer'
  // },
  // {
  //   key: 'sponsors',
  //   label: 'Sponsor updates',
  //   description: 'Messages from event sponsors'
  // }
];

const TYPE_TO_CATEGORY = {
  chat: 'messages',
  'session-start': 'sessions',
  'session-end': 'sessions',
  'session-reminder': 'sessions',
  'schedule-change': 'sessions',
  promoted: 'announcements',
  info: 'announcements',
  poll: 'polls',
  sponsor: 'sponsors'
};

function categoryForType(type) {
  return TYPE_TO_CATEGORY[type] || 'announcements';
}

function defaultPreferences() {
  // All categories on by default — users opt OUT.
  return CATEGORIES.reduce((acc, c) => {
    acc[c.key] = true;
    return acc;
  }, {});
}

// Keep only keys that are actual, current categories. Guards against stale
// keys (e.g. categories that were removed/commented out, like 'polls' or
// 'sponsors') lingering in localStorage or an old DB record and being
// re-persisted. Applied everywhere preferences enter state or get saved.
const CATEGORY_KEYS = new Set(CATEGORIES.map((c) => c.key));
function sanitizePreferences(prefs) {
  const out = {};
  if (prefs && typeof prefs === 'object') {
    Object.keys(prefs).forEach((key) => {
      if (CATEGORY_KEYS.has(key)) out[key] = prefs[key] !== false;
    });
  }
  return out;
}

// ── localStorage persistence (per user) ─────────────────────────────
function prefsKey(userId) {
  return `tappin_notif_prefs_${userId || 'guest'}`;
}
function logKey(userId) {
  return `tappin_notif_log_${userId || 'guest'}`;
}
function loadPrefs(userId) {
  try {
    const raw = localStorage.getItem(prefsKey(userId));
    if (!raw) return defaultPreferences();
    const parsed = JSON.parse(raw);
    // Merge over defaults so newly-added categories default to ON, and drop
    // any stale keys that are no longer categories.
    return { ...defaultPreferences(), ...sanitizePreferences(parsed) };
  } catch (e) {
    return defaultPreferences();
  }
}
function savePrefs(userId, prefs) {
  try {
    localStorage.setItem(prefsKey(userId), JSON.stringify(prefs || {}));
  } catch (e) {
    /* ignore quota / privacy-mode errors */
  }
}
function loadLog(userId) {
  try {
    const raw = localStorage.getItem(logKey(userId));
    const arr = raw ? JSON.parse(raw) : [];
    return Array.isArray(arr) ? arr.slice(0, MAX_ITEMS) : [];
  } catch (e) {
    return [];
  }
}
function saveLog(userId, items) {
  try {
    localStorage.setItem(
      logKey(userId),
      JSON.stringify((items || []).slice(0, MAX_ITEMS))
    );
  } catch (e) {
    /* ignore */
  }
}

// ── Sound helper ────────────────────────────────────────────────────
// Plays a short notification chime. Uses HTML5 Audio with a base64
// data URI for the broadest compatibility — Web Audio API gets blocked
// by autoplay policy in many browsers until a user gesture, but a
// pre-loaded <audio> element with `playsInline` is more permissive.
//
// We also warm up an AudioContext on first interaction as a fallback
// so the chime plays even if HTML5 Audio is blocked.

// Build a short two-tone chime as a WAV ArrayBuffer at module load,
// expose it as an object URL, and feed it to an <Audio> element. WAV
// generation lets us avoid shipping a binary asset while still using
// HTML5 Audio (more permissive under autoplay policy than Web Audio).
function buildChimeWavUrl() {
  if (typeof window === 'undefined') return '';
  try {
    const sampleRate = 22050;
    const duration = 0.28; // seconds
    const numSamples = Math.floor(sampleRate * duration);
    const samples = new Float32Array(numSamples);
    // Two overlapping sine tones with simple envelopes.
    for (let i = 0; i < numSamples; i++) {
      const t = i / sampleRate;
      // Tone 1: 880 Hz, fades 0→1→0 over 0–120ms
      let env1 = 0;
      if (t < 0.12) {
        env1 = t < 0.02 ? t / 0.02 : Math.max(0, 1 - (t - 0.02) / 0.1);
      }
      const tone1 = Math.sin(2 * Math.PI * 880 * t) * env1 * 0.45;
      // Tone 2: 1174 Hz, starts at 100ms, fades to 0 by 280ms
      let tone2 = 0;
      if (t >= 0.1) {
        const tt = t - 0.1;
        const env2 =
          tt < 0.02 ? tt / 0.02 : Math.max(0, 1 - (tt - 0.02) / 0.15);
        tone2 = Math.sin(2 * Math.PI * 1174 * t) * env2 * 0.4;
      }
      samples[i] = tone1 + tone2;
    }
    // Pack into 16-bit PCM WAV.
    const buffer = new ArrayBuffer(44 + numSamples * 2);
    const view = new DataView(buffer);
    const writeStr = (offset, s) => {
      for (let i = 0; i < s.length; i++)
        view.setUint8(offset + i, s.charCodeAt(i));
    };
    writeStr(0, 'RIFF');
    view.setUint32(4, 36 + numSamples * 2, true);
    writeStr(8, 'WAVE');
    writeStr(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true); // PCM
    view.setUint16(22, 1, true); // mono
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeStr(36, 'data');
    view.setUint32(40, numSamples * 2, true);
    let offset = 44;
    for (let i = 0; i < numSamples; i++) {
      let v = Math.max(-1, Math.min(1, samples[i]));
      view.setInt16(offset, v * 0x7fff, true);
      offset += 2;
    }
    const blob = new Blob([buffer], { type: 'audio/wav' });
    return URL.createObjectURL(blob);
  } catch (e) {
    return '';
  }
}

// Primary notification sound — a bundled audio file in public/assets
// (`public/assets/notifications.mp3`, served at `/assets/notifications.mp3`).
// If it's missing/unsupported we fall back to the synthesized two-tone
// chime so notifications always beep.
const NOTIFICATION_SOUND_URL = '/assets/notifications.mp3';

let chimeAudio = null;
function getChimeAudio() {
  if (typeof window === 'undefined' || !NOTIFICATION_SOUND_URL) return null;
  if (chimeAudio) return chimeAudio;
  try {
    chimeAudio = new Audio(NOTIFICATION_SOUND_URL);
    chimeAudio.preload = 'auto';
    chimeAudio.volume = 0.6;
    // Graceful fallback: if the bundled file can't load, swap in the
    // synthesized chime so the notification still makes a sound.
    chimeAudio.addEventListener('error', () => {
      const fallback = buildChimeWavUrl();
      if (fallback && chimeAudio) chimeAudio.src = fallback;
    });
    return chimeAudio;
  } catch (e) {
    return null;
  }
}

let audioUnlocked = false;
function unlockAudio() {
  if (audioUnlocked) return;
  audioUnlocked = true;
  // Play (and immediately pause) once on the first user gesture so
  // the browser permits future autoplay programmatically. Muted, because on
  // mobile the pause lands after playback has started and the chime was
  // audible on the first tap of any page (e.g. the login page).
  try {
    const a = getChimeAudio();
    if (!a) return;
    a.muted = true;
    const playPromise = a.play();
    const reset = () => {
      try {
        a.pause();
        a.currentTime = 0;
      } catch (e) {
        /* ignore */
      }
      a.muted = false;
    };
    if (playPromise && playPromise.then) {
      playPromise.then(reset).catch(() => {
        a.muted = false;
      });
    } else {
      reset();
    }
  } catch (e) {
    /* ignore */
  }
}

if (typeof document !== 'undefined') {
  const onFirstGesture = () => {
    unlockAudio();
    document.removeEventListener('click', onFirstGesture, true);
    document.removeEventListener('keydown', onFirstGesture, true);
    document.removeEventListener('touchstart', onFirstGesture, true);
  };
  document.addEventListener('click', onFirstGesture, true);
  document.addEventListener('keydown', onFirstGesture, true);
  document.addEventListener('touchstart', onFirstGesture, true);
}

function playNotificationSound() {
  try {
    const a = getChimeAudio();
    if (!a) return;
    // Rewind to start so back-to-back notifications each play in full.
    try {
      a.currentTime = 0;
    } catch (e) {
      /* ignore */
    }
    const p = a.play();
    if (p && p.catch)
      p.catch(() => {
        /* autoplay blocked — silent */
      });
  } catch (e) {
    // Silently ignore — sound is best-effort
  }
}

// ── Browser notification for background tabs ────────────────────────
// When the user has minimised the platform or switched tabs, surface a
// native OS notification so they don't miss important events.
//
// We route it through the service-worker registration's showNotification()
// whenever one is available, so a click is handled by push-sw.js — which
// focuses/opens the app and navigates to the right place. A raw page-context
// `new Notification()` has no reliable click-to-open behaviour and, without a
// `tag`, stacks a fresh banner for every event (the "notifications loop").
//
// `data` carries { tag, url } so notifications coalesce by entity and clicks
// deep-link correctly. We never call requestPermission() here — enrolment is
// an explicit opt-in handled by the PushNotifications store.
async function showBrowserNotification(title, body, data = {}) {
  try {
    if (!('Notification' in window) || Notification.permission !== 'granted') {
      return;
    }

    const tag = data.tag || 'tappin-notification';
    const url = data.url || '/';
    const options = {
      body: body || '',
      icon: '/assets/favicon/apple-touch-icon.png',
      badge: '/assets/favicon/favicon-32x32.png',
      tag,
      renotify: true,
      data: { url, type: data.type || null, meta: data.meta || null }
    };

    // Always show via the service-worker registration so the click is
    // handled by push-sw.js (focus/open the app). Register on demand if the
    // worker isn't up yet — the page-context fallback below has unreliable
    // click behaviour (window.focus() is blocked in modern browsers).
    if ('serviceWorker' in navigator) {
      let registration = await navigator.serviceWorker.getRegistration();
      if (!registration || !registration.showNotification) {
        try {
          registration = await navigator.serviceWorker.register('/push-sw.js');
        } catch (e) {
          registration = null;
        }
      }
      if (registration && registration.showNotification) {
        await registration.showNotification(title, options);
        return;
      }
    }

    // Last-resort fallback (no SW available at all).
    const notification = new Notification(title, options);
    notification.onclick = () => {
      try {
        window.focus();
      } catch (e) {
        /* ignore */
      }
      if (url && url !== '/') window.location.href = url;
      notification.close();
    };
  } catch (e) {
    // Silently ignore — background notification is best-effort.
  }
}

// ── De-duplication of redundant dispatches ──────────────────────────
// The app raises the SAME logical notification from several independent
// safety-net paths (a deep watcher, store-mutation subscriptions and a
// 5s poller — see Navbar.vue), and there can be more than one Navbar
// instance. Each path calls `add`, which would otherwise replay the
// sound, toast AND OS notification 3–5× for ONE event. We collapse
// identical dispatches seen within a short window down to a single one.
//
// Module-scoped (shared across all component instances) so it de-dupes
// even when multiple Navbars are mounted.
const DEDUPE_WINDOW_MS = 10000;
const recentDispatchAt = new Map(); // signature -> timestamp

function isDuplicateDispatch(signature) {
  const now = Date.now();
  // Prune stale entries so the map can't grow unbounded.
  for (const [key, ts] of recentDispatchAt) {
    if (now - ts > DEDUPE_WINDOW_MS) recentDispatchAt.delete(key);
  }
  if (recentDispatchAt.has(signature)) return true;
  recentDispatchAt.set(signature, now);
  return false;
}

export const state = () => ({
  items: [],
  // Whose log/prefs are currently loaded (set by `hydrate`).
  userId: null,
  preferences: defaultPreferences()
});

export const getters = {
  getItems: (state) => state.items,
  unreadCount: (state) => state.items.filter((n) => !n.read).length,
  // Settings UI
  categories: () => CATEGORIES,
  getPreferences: (state) => state.preferences,
  isCategoryEnabled: (state) => (category) =>
    state.preferences[category] !== false,
  isTypeEnabled: (state) => (type) =>
    state.preferences[categoryForType(type)] !== false
};

export const mutations = {
  add(state, payload) {
    // De-dupe by key when provided so the same chat thread doesn't pile up.
    // Always replace the array reference so Vue 2 reactivity picks up
    // additions and any subsequent property mutations on the items.
    let next = state.items.slice();
    if (payload.dedupeKey) {
      next = next.filter((n) => n.dedupeKey !== payload.dedupeKey);
    }
    next.unshift(payload);
    if (next.length > MAX_ITEMS) next.length = MAX_ITEMS;
    state.items = next;
    saveLog(state.userId, state.items);
  },
  markAllRead(state) {
    // Replace the array with new objects so reactivity is guaranteed —
    // mutating `n.read` in place can be missed for items whose read
    // property wasn't pre-declared when they were added.
    state.items = state.items.map((n) => ({ ...n, read: true }));
    saveLog(state.userId, state.items);
  },
  markRead(state, id) {
    state.items = state.items.map((n) =>
      n.id === id ? { ...n, read: true } : n
    );
    saveLog(state.userId, state.items);
  },
  remove(state, id) {
    state.items = state.items.filter((n) => n.id !== id);
    saveLog(state.userId, state.items);
  },
  clear(state) {
    state.items = [];
    saveLog(state.userId, state.items);
  },
  // ── Persistence / preferences ─────────────────────────────────────
  hydrate(state, userId) {
    state.userId = userId || 'guest';
    state.preferences = loadPrefs(state.userId);
    // Merge the persisted history with anything already shown this
    // session — the profile (and thus userId) often resolves AFTER the
    // first notifications fire, and we must NOT wipe those from the bell.
    const persisted = loadLog(state.userId);
    const seen = new Set(state.items.map((n) => n.id));
    const merged = state.items.concat(
      persisted.filter((n) => n && !seen.has(n.id))
    );
    state.items = merged.slice(0, MAX_ITEMS);
    saveLog(state.userId, state.items);
  },
  setPreference(state, { category, enabled }) {
    state.preferences = { ...state.preferences, [category]: !!enabled };
    savePrefs(state.userId, state.preferences);
  },
  // Replace the whole preference map (used when loading the server copy).
  // Merge sanitized server values over defaults so any category the server
  // doesn't mention stays on, and stale/unknown keys are dropped.
  setPreferences(state, serverPreferences) {
    state.preferences = {
      ...defaultPreferences(),
      ...sanitizePreferences(serverPreferences)
    };
    savePrefs(state.userId, state.preferences);
  }
};

export const actions = {
  add({ commit, state, rootGetters }, notification) {
    if (!notification) return;
    const type = notification.type || 'info';

    // Respect the participant's notification preferences — silently drop
    // categories they've opted out of (no entry, no sound, no badge).
    // Guard against a missing preferences object so a notification is
    // never lost to an unexpected error.
    const prefs = state.preferences || {};
    if (prefs[categoryForType(type)] === false) return;

    // Collapse the redundant multi-source dispatches (deep watcher +
    // store subscriptions + poller, possibly across several Navbar
    // instances) for the SAME event into ONE sound/toast/OS notification.
    // Dedupe by CONTENT (key + type + title + message) so it works even
    // when a path omits dedupeKey; a different message body still notifies.
    const signature = `${notification.dedupeKey || ''}|${type}|${
      notification.title || ''
    }|${notification.message || ''}`;
    if (isDuplicateDispatch(signature)) return;

    // "Push notifications" feature gate (workspace level). When OFF the
    // notification is STILL recorded in the bell/list (visible in the
    // notification centre), but with NO alerting: no toast, no sound, no OS
    // push. `silent` marks the entry so the toast subscriber in App.vue skips
    // it. Fail-OPEN: only an EXPLICIT `false` suppresses alerting, so a missing
    // or not-yet-loaded feature flag never silently kills the chime.
    let pushFeatureOn = true;
    try {
      const eventFeatures = rootGetters['CurrentEvent/features'];
      pushFeatureOn = eventFeatures
        ? eventFeatures.pushNotificationEnabled !== false
        : true;
    } catch (e) {
      pushFeatureOn = true;
    }

    const entry = {
      id:
        notification.id ||
        `n_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
      type,
      title: notification.title || '',
      message: notification.message || '',
      timestamp: notification.timestamp || Date.now(),
      read: false,
      route: notification.route || null,
      dedupeKey: notification.dedupeKey || null,
      meta: notification.meta || null,
      silent: !pushFeatureOn
    };
    // Always record it in the list/bell.
    commit('add', entry);

    // Alerting (toast + sound + OS push) only when the feature is on.
    if (!pushFeatureOn) return;

    // Play the chime sound on every new notification
    playNotificationSound();

    // If the tab is hidden / minimised, also show a native OS notification so
    // the user doesn't miss it. Pass a stable `tag` (so repeats coalesce
    // instead of stacking) and a `url` (so a click deep-links via the SW).
    if (document.hidden) {
      showBrowserNotification(entry.title, entry.message, {
        tag: entry.dedupeKey || entry.id,
        url: entry.route || '/',
        type: entry.type,
        meta: entry.meta
      });
    }
  },
  // Load this user's persisted log + preferences. Call once the user is
  // known (e.g. from the Navbar on mount). Loads the localStorage cache
  // immediately, then reconciles with the server copy (source of truth).
  hydrate({ commit, dispatch }, userId) {
    commit('hydrate', userId);
    return dispatch('loadServerPreferences', userId);
  },
  // Fetch the server-stored preferences for this participant + event and
  // apply them. No record on the server ⇒ empty map ⇒ defaults (all on)
  // are kept. Never writes; purely a read reconcile. Failures are ignored
  // so notifications keep working offline / if the endpoint is unreachable.
  loadServerPreferences({ commit, rootGetters }, userId) {
    const eventId = rootGetters['CurrentEvent/getId'];
    if (!eventId || !userId) return Promise.resolve();
    return ApiClient.getNotificationPreferences(eventId)
      .then((res) => {
        const server = res && res.data;
        if (
          server &&
          typeof server === 'object' &&
          Object.keys(server).length
        ) {
          commit('setPreferences', server);
        }
      })
      .catch(() => {
        /* keep localStorage / defaults */
      });
  },
  // Explicit user toggle: update state + localStorage, then persist to the
  // server so the choice follows them across devices. This is the ONLY path
  // that creates/updates the server record (per the "store only on explicit
  // change" requirement).
  setPreference({ commit, state, rootGetters }, payload) {
    commit('setPreference', payload);
    const eventId = rootGetters['CurrentEvent/getId'];
    if (!eventId) return;
    // Persist only current category keys — never re-store stale ones.
    ApiClient.saveNotificationPreferences(
      eventId,
      sanitizePreferences(state.preferences)
    ).catch(() => {
      /* best-effort — localStorage still holds the choice */
    });
  },
  markAllRead({ commit }) {
    commit('markAllRead');
  },
  markRead({ commit }, id) {
    commit('markRead', id);
  },
  remove({ commit }, id) {
    commit('remove', id);
  },
  clear({ commit }) {
    commit('clear');
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
